import { WSSelectProps } from "@wingspanhq/fe-component-library";
import {
  AccountPurpose,
  AnnualRevenueRange,
  BusinessOperatingGeography,
  CashFlowDescription,
  MonthlyTransactionVolume,
  PrimaryCustomerType,
  SourceOfFunds
} from "@wingspanhq/users/dist/lib/interfaces";

export interface EDDQuestionnaireFormData {
  purposeOfAccount: AccountPurpose;
  cashFlow: CashFlowDescription;
  monthlyTransactionVolume: MonthlyTransactionVolume;
  sourceOfFunds: SourceOfFunds;
  annualRevenue: AnnualRevenueRange;
  businessOperatingGeography: BusinessOperatingGeography;
  primaryCustomerType: PrimaryCustomerType;
  prohibitedJurisdictionExposure: ProhibitedJurisdictionExposure;
  prohibitedJurisdictionExposureDetails?: string;
}

export enum ProhibitedJurisdictionExposure {
  No = "No",
  Yes = "Yes",
  Unsure = "Unsure"
}

const accountPurposeOptions: WSSelectProps["options"] = [
  {
    value: AccountPurpose.ReceivingPayments,
    label: "Receiving payments from customers/clients"
  },
  {
    value: AccountPurpose.PayingSuppliersContractorsOrVendors,
    label: "Paying suppliers, contractors, or vendors"
  },
  {
    value: AccountPurpose.ManagingBusinessSavingsOrInvestments,
    label: "Managing business savings or investments"
  },
  {
    value: AccountPurpose.SendingOrReceivingInternationalPayments,
    label: "Sending or receiving international payments"
  },
  {
    value: AccountPurpose.HandlingMostlyCashRelatedTransactions,
    label: "Handling mostly cash-related transactions"
  }
];

const cashFlowOptions: WSSelectProps["options"] = [
  {
    value: CashFlowDescription.StruggleToCoverExpenses,
    label: "We often struggle to cover expenses or rely on outside funding"
  },
  {
    value: CashFlowDescription.InconsistentRevenueVariesMonthly,
    label: "Our revenue is inconsistent and varies month-to-month"
  },
  {
    value: CashFlowDescription.FairlySteadySlimMargins,
    label: "Our revenue is fairly steady, but we operate with slim margins"
  },
  {
    value: CashFlowDescription.ConsistentRevenueHealthyReserves,
    label: "Our revenue is consistent and we maintain healthy reserves"
  },
  {
    value: CashFlowDescription.PrimarilyDealInCash,
    label: "We primarily deal in cash transactions"
  },
  {
    value: CashFlowDescription.HardToSayDontTrackClosely,
    label: "It's hard to say / We don't track closely"
  }
];

const monthlyTransactionVolumeOptions: WSSelectProps["options"] = [
  {
    value: MonthlyTransactionVolume.Between10KAnd50K,
    label: "$10,000 - $50,000"
  },
  {
    value: MonthlyTransactionVolume.Between50KAnd100K,
    label: "$50,000 - $100,000"
  },
  {
    value: MonthlyTransactionVolume.Between100KAnd500K,
    label: "$100,000 - $500,000"
  },
  { value: MonthlyTransactionVolume.Over500K, label: "Over $500,000" }
];
const sourceOfFundsOptions: WSSelectProps["options"] = [
  {
    value: SourceOfFunds.SalesRevenueFromProductsOrServices,
    label: "Sales revenue from products or services"
  },
  {
    value: SourceOfFunds.CapitalFromInvestorsOrShareholders,
    label: "Capital from investors or shareholders"
  },
  {
    value: SourceOfFunds.LoansOrLinesOfCredit,
    label: "Loans or lines of credit"
  },
  {
    value: SourceOfFunds.GovernmentOrPrivateGrants,
    label: "Government or private grants"
  },
  {
    value: SourceOfFunds.PrimarilyCashReceipts,
    label: "Primarily cash receipts"
  }
];

const annualRevenueRangeOptions: WSSelectProps["options"] = [
  { value: AnnualRevenueRange.UpTo250K, label: "Up to $250,000" },
  {
    value: AnnualRevenueRange.Between250KAnd500K,
    label: "$250,000 - $500,000"
  },
  {
    value: AnnualRevenueRange.Between500KAnd1M,
    label: "$500,000 - $1,000,000"
  },
  {
    value: AnnualRevenueRange.Between1MAnd5M,
    label: "$1,000,000 - $5,000,000"
  },
  { value: AnnualRevenueRange.Over5M, label: "Over $5,000,000" }
];

const businessOperatingGeographyOptions: WSSelectProps["options"] = [
  {
    value: BusinessOperatingGeography.WithinLocalCityOrState,
    label: "Primarily within local city or state"
  },
  {
    value: BusinessOperatingGeography.AcrossMultipleUSStates,
    label: "Across multiple U.S. states"
  },
  {
    value: BusinessOperatingGeography.AcrossTheUnitedStates,
    label: "Across the United States"
  },
  {
    value: BusinessOperatingGeography.InternationalCanadaWesternEurope,
    label: "International (Canada, Western Europe, Australia)"
  },
  {
    value:
      BusinessOperatingGeography.InternationalLatinAmericaAsiaAfricaMiddleEast,
    label: "International (Latin America, Asia, Africa, Middle East)"
  },
  {
    value: BusinessOperatingGeography.PrimaryInternationalLimitedUSPresence,
    label: "Primarily international with limited U.S. presence"
  }
];

const primaryCustomerTypeOptions: WSSelectProps["options"] = [
  {
    value: PrimaryCustomerType.IndividualConsumersB2C,
    label: "Individual consumers (B2C)"
  },
  {
    value: PrimaryCustomerType.SmallOrMediumBusinessesB2B,
    label: "Small or medium-sized businesses (B2B)"
  },
  {
    value: PrimaryCustomerType.LargeEnterprisesOrGovernmentAgencies,
    label: "Large enterprises or government agencies"
  },
  {
    value: PrimaryCustomerType.PrimarilyCashPayingCustomers,
    label: "Primarily cash-paying customers"
  },
  {
    value: PrimaryCustomerType.PrimarilyOnlineDigitalCustomers,
    label: "Primarily online/digital customers"
  },
  {
    value: PrimaryCustomerType.InternationalCustomersOutsideUS,
    label: "International customers (outside the U.S.)"
  },
  {
    value: PrimaryCustomerType.CustomersInHighCashFlowIndustries,
    label: "Customers in high-cash-flow industries"
  }
];

const prohibitedJurisdictionExposureOptions: WSSelectProps["options"] = [
  {
    value: ProhibitedJurisdictionExposure.No,
    label:
      "No - we do not have clients, contractors, or vendors in any of these jurisdictions"
  },
  {
    value: ProhibitedJurisdictionExposure.Yes,
    label: "Yes - please describe"
  },
  {
    value: ProhibitedJurisdictionExposure.Unsure,
    label: "Unsure - I would need to verify"
  }
];

export const EDD_FIELDS: {
  title: string;
  name: string;
  label: string;
  componentProps: Omit<WSSelectProps, "name" | "onChange" | "value">;
}[] = [
  {
    title: "Account purpose",
    name: "purposeOfAccount",
    label:
      "In addition to payroll processing, what is the primary other purpose you have for using our services?",
    componentProps: {
      options: accountPurposeOptions
    }
  },
  {
    title: "Cash flow",
    name: "cashFlow",
    label: "How would you best describe your business's cash flow?",
    componentProps: {
      options: cashFlowOptions
    }
  },
  {
    title: "Monthly transaction volume",
    name: "monthlyTransactionVolume",
    label:
      "What is your business's expected monthly transaction volume, including payroll and other transactions?",
    componentProps: {
      options: monthlyTransactionVolumeOptions
    }
  },
  {
    title: "Source of funds",
    name: "sourceOfFunds",
    label:
      "What are the primary sources of funds for your business? (Select all that apply)",
    componentProps: {
      options: sourceOfFundsOptions
    }
  },
  {
    title: "Annual revenue range",
    name: "annualRevenue",
    label: "What is your company's annual revenue range?",
    componentProps: {
      options: annualRevenueRangeOptions
    }
  },
  {
    title: "Business operating geography",
    name: "businessOperatingGeography",
    label: "Where does your business primarily operate and transact?",
    componentProps: {
      options: businessOperatingGeographyOptions
    }
  },
  {
    title: "Primary customers",
    name: "primaryCustomerType",
    label: "Who are your primary customers?",
    componentProps: {
      options: primaryCustomerTypeOptions
    }
  },
  {
    title: "Prohibited jurisdiction exposure",
    name: "prohibitedJurisdictionExposure",
    label:
      "Does your business have any clients, contractors, or vendors located in, or do you process payments to or from, any of the following jurisdictions: Cuba, Iran, North Korea, Syria, Russia, Belarus, or any other country on the OFAC Specially Designated Nationals list or Wingspan's Prohibited Countries List?",
    componentProps: {
      options: prohibitedJurisdictionExposureOptions
    }
  }
];
